import { filterAndMapEmails, removeRedundantEmail } from "../../../helpers/email";
import { msGraphGet, msGraphPost } from "../graph.general";

const getFolderEmails = async (next_link, filter = "", folderName) => {
  const link = next_link
    ? next_link
    : "/me/mailFolders/" +
      folderName +
      "/messages/?" +
      filter +
      "&$select=sender,subject,createdDateTime,hasAttachments,isRead";

  return await msGraphGet(link, !next_link);
};
/**
 * Retrieves mixed emails using search API.
 * @param {string|null} next_link - Pagination link or null for initial request.
 * @param {string} filter - Search query string.
 * @param {number} from - Starting index for pagination.
 * @param {number} size - Number of results to fetch.
 * @returns {Promise<Object>} Search results.
 */
const getMixedEmails = async (next_link, filter = "", from = 0, size = 20) => {
  const link = next_link ? next_link : "/search/query";
  return await msGraphPost(link, {
    requests: [
      {
        entityTypes: ["message"],
        query: { queryString: filter || "*" },
        from: from,
        size: size,
      },
    ],
  });
};

/**
 * Retrieves attachments for a given email.
 *
 * @param {string} emailId - The ID of the email.
 * @returns {Promise<Object[]>} A list of attachment metadata.
 * @throws {Error} If the request fails.
 */
async function getEmailAttachments(emailId) {
  const attachmentsApiPath = `/me/messages/${emailId}/attachments`;
  const attachmentsResponse = await msGraphGet(attachmentsApiPath);
  return attachmentsResponse.value || [];
}

/**
 * Searches messages in Microsoft Graph.
 *
 * @param {string} queryString - The search keyword or phrase.
 * @param {number} skip - The number of results to skip (for pagination).
 * @param {string} entityType - The type of entity to search (default: "message").
 * @returns {Promise<Object>} A promise resolving to the search results.
 * @throws {Error} If the request fails.
 */
async function searchGraphMessages(
  queryString,
  from = 0,
  size = 25,
  entityType = "message"
) {
  const apiPath = "/search/query";

  const myHeaders = {
    "Content-Type": "application/json",
  };

  // Microsoft Graph Search API requires a non-empty queryString
  // Use "*" to fetch all messages when no search text is provided
  const effectiveQuery = queryString && queryString.trim() ? queryString : "*";

  const raw = JSON.stringify({
    requests: [
      {
        entityTypes: ["message"],
        query: {
          queryString: effectiveQuery,
        },
        from: from,
        size: size,
      },
    ],
  });

  return await msGraphPost(apiPath, raw, true, myHeaders, 1);
}

/**
 * Searches messages in Microsoft Graph.
 *
 * @param {string} queryString - The search keyword or phrase.
 * @param {number} skip - The number of results to skip (for pagination).
 * @param {string} entityType - The type of entity to search (default: "message").
 * @returns {Promise<Object>} A promise resolving to the search results.
 * @throws {Error} If the request fails.
 */
/**
 * Searches for email addresses of people based on a query string.
 * Optimized to reduce API calls while still providing good coverage.
 * @param {string} queryString - The search query.
 * @returns {Promise<Array<string>>} Array of unique email addresses.
 */
async function searchGraphPeopleEmail(queryString) {
  const apiPath = "/search/query";
  const myHeaders = {
    "Content-Type": "application/json",
  };

  // Reduced from 400 to 100 results (4 batches instead of 16)
  // This significantly reduces API load while still providing good autocomplete
  const size = 25;
  const totalResults = 100;
  const requestsNeeded = Math.ceil(totalResults / size);

  const requests = Array.from({ length: requestsNeeded }, (_, i) => {
    const from = i * size;
    const raw = JSON.stringify({
      requests: [
        {
          entityTypes: ["message"],
          query: { queryString },
          from: from,
          size: size,
          enableTopResults: true,
          Fields: ["sender", "from", "replyTo"],
        },
      ],
    });

    return msGraphPost(apiPath, raw, true, myHeaders, 1)
      .then((resp) => {
        var hits = null;
        if (resp && resp.value && resp.value[0] && resp.value[0].hitsContainers && resp.value[0].hitsContainers[0]) {
          hits = resp.value[0].hitsContainers[0].hits;
        }
        return hits ? filterAndMapEmails(queryString, hits) : [];
      })
      .catch(() => []);
  });

  // Execute all requests in parallel and collect results
  const results = await Promise.all(requests);

  // Merge and deduplicate results
  return removeRedundantEmail(results.flat());
}

export {
  getMixedEmails,
  getFolderEmails,
  searchGraphMessages,
  getEmailAttachments,
  searchGraphPeopleEmail,
};
